import { Component } from '@angular/core';
import {
  Answer,
  AssetType,
  Exercise,
  NxTestSessionDetailedResultV2Model,
  StudentResultsClient,
  ExamReportsClient,
  UserAnswer,
  AnswerOption,
  Question,
} from '../../../generated-client/generated-api-client';
import { SimpleModalComponent } from 'ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { ErrorProcessorService } from '../../../services/error-processor.service';
import { AnswerColor } from '../../../enums/AnswerColor';
import { StudentExamDataService } from '../../../services/data-services/student-exam-data.service';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import '../../../../assets/fonts/Roboto-Regular-normal.js';

export interface DetailedResultV2PreviewModal {
  resultId: string;
  courseTitle: string;
  studentName: string;
}

@Component({
  selector: 'app-detailed-resultV2-preview-modal',
  templateUrl: './detailed-resultV2-preview-modal.component.html',
  styleUrls: ['./detailed-resultV2-preview-modal.component.sass'],
})
export class DetailedResultV2PreviewModalComponent
  extends SimpleModalComponent<DetailedResultV2PreviewModal, boolean>
  implements DetailedResultV2PreviewModal {
  AnswerColor = AnswerColor;
  model?: NxTestSessionDetailedResultV2Model;
  resultId!: string;
  isLoading: boolean = true;
  precision: number = 0;
  courseTitle!: string;
  isPdfLoading = false;
  studentName!: string;

  constructor(
    private _toastr: ToastrService,
    private _studentResultsClient: StudentResultsClient,
    private _examReportsClient: ExamReportsClient,
    private _studentExamDataService: StudentExamDataService,
    private _errorProcessor: ErrorProcessorService
  ) {
    super();
  }

  ngOnInit(): void {
    this._examReportsClient.get(this.examReportId).subscribe(next => {
      this.precision = next.percentagePrecision || 0;
    });
    this._studentResultsClient.detailedResultV2(this.resultId).subscribe(
      model => {
        this.model = model;
        this.isLoading = false;
      },
      error => {
        this._toastr.error('Váratlan hiba történt');
        this.close();
      }
    );
  }

  get AssetType() {
    return AssetType;
  }

  get examReportId() {
    return this._studentExamDataService.examReportId.value;
  }

  save() {
    this._studentResultsClient.detailedResultV2(this.resultId).subscribe(
      next => {
        const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(next));
        const dlAnchorElem = document.getElementById('downloadAnchorElem') as HTMLAnchorElement;
        dlAnchorElem.setAttribute('href', dataStr);
        dlAnchorElem.setAttribute('download', `eredmény-${this.resultId}.json`);
        dlAnchorElem.click();
      },
      error => {
        const statusMessages = {
          404: 'Nem található részletes eredmény fájl.',
        };
        return this._errorProcessor.processError(error, true, false, statusMessages);
      }
    );
  }

  formatResult(data: number) {
    return (Math.trunc(data * Math.pow(10, this.precision)) / Math.pow(10, this.precision)).toFixed(this.precision);
  }

  getQuestionText(options: AnswerOption[] | undefined, id: number | undefined) {
    return this.getAnswerOption(options, id)?.description ?? (id == null ? '-' : `Ismeretlen válasz (#${id})`);
  }

  getAnswerOption(options: AnswerOption[] | undefined, id: number | undefined): AnswerOption | undefined {
    if (id == null) return undefined;
    return options?.find(option => option.id === id);
  }

  getMultiChoiceAnswerOptionColor(questions: Question[] | undefined, id: number | undefined) {
    var color;
    if (questions == undefined || id == undefined) return 'red';
    questions[0]?.correctAnswers?.indexOf(id) != undefined && questions[0]?.correctAnswers?.indexOf(id) > -1
      ? (color = 'green')
      : (color = 'red');
    return color;
  }

  geFillInSelectAnswerOptionColor(questions: Question[] | undefined, id: number | undefined) {
    var color;
    if (questions == undefined || id == undefined) return 'red';
    questions[0]?.correctAnswers?.indexOf(id) != undefined && questions[0]?.correctAnswers?.indexOf(id) > -1
      ? (color = 'green')
      : (color = 'red');
    return color;
  }

  answerColor(exercise: Exercise, answer: Answer, userAnswers: UserAnswer[] | undefined): AnswerColor {
    let result = AnswerColor.Default;
    let userAnswerIds: number[] = [];
    const userAnswer = userAnswers?.find(x => x.exerciseId == exercise.id);

    if (answer.id === undefined) return result;

    // helyes válaszok zöldre színezése
    if (exercise.correctAnswerId != undefined && exercise.correctAnswerId >= 0) {
      if (answer.id == exercise.correctAnswerId) {
        result = AnswerColor.Green;
      }
    }

    if (exercise.correctAnswerIds) {
      if (exercise.correctAnswerIds.includes(answer.id!)) {
        result = AnswerColor.Green;
      }
    }

    // vizsgázó válaszait érintő színezések
    if (userAnswer) {
      if (userAnswer.answerId !== undefined && userAnswer.answerId !== null && userAnswer.answerId >= 0) {
        userAnswerIds = [userAnswer.answerId];
      }
      if (userAnswer.answerIds) {
        userAnswerIds = userAnswer.answerIds;
      }
      if (userAnswer.fillInAnswers && userAnswer.fillInAnswers.length > 0) {
        const correctFillInAnswerIds = userAnswer.fillInAnswers
          .filter(x => x.isCorrect && x.answerId != undefined)
          .map(x => x.answerId!);
        if (correctFillInAnswerIds && correctFillInAnswerIds.length > 0) {
          userAnswerIds = correctFillInAnswerIds;
        }

        if (userAnswerIds.includes(answer.id)) {
          result = AnswerColor.GreenWithTick;
        } else {
          result = AnswerColor.Red;
        }
      }

      if (result === AnswerColor.Green && userAnswerIds.includes(answer.id)) {
        result = AnswerColor.GreenWithTick;
      } else if (
        result !== AnswerColor.Green &&
        result !== AnswerColor.GreenWithTick &&
        userAnswerIds.includes(answer.id)
      ) {
        result = AnswerColor.Red;
      }
    }
    return result;
  }

  async exportPdf() {
    const infoToast = this._toastr.info('PDF generálás folyamatban...', undefined, { timeOut: 0, closeButton: true });
    this.isPdfLoading = true;

    try {
      const source = document.querySelector('.modal-body') as HTMLElement;
      const imageMap = await this.buildImageDataUrlMap(source);

      // Build the list of atomic blocks: the summary rows + each question (.accordion-item)
      const blocks: HTMLElement[] = [];
      source.querySelectorAll(':scope > .row').forEach(row => {
        const items = row.querySelectorAll('.accordion-item');
        if (items.length) items.forEach(i => blocks.push(i as HTMLElement));
        else blocks.push(row as HTMLElement);
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pageW - margin * 2;
      const gap = 3;
      let y = margin;

      const onclone = (clonedDoc: Document) => {
        const style = clonedDoc.createElement('style');
        style.textContent = `
        * { font-family:'Roboto-Regular',sans-serif!important;
            font-size:12px!important; line-height:1.35!important; }
        h2,h4 { font-size:14px!important; }
        .btn-lg { font-size:13px!important; padding:6px 10px!important; }
        p,div,span,td,h2 { word-break:break-word!important; overflow-wrap:break-word!important; }
        img { max-width:100%!important; max-height:220px!important; height:auto!important; }
      `;
        clonedDoc.head.appendChild(style);
        clonedDoc.querySelectorAll('.material-icons, .material-icons-outlined')
          .forEach(i => ((i as HTMLElement).style.display = 'none'));
        clonedDoc.querySelectorAll('img').forEach(img => {
          const dataUrl = imageMap.get(img.getAttribute('src') || '');
          if (dataUrl) img.setAttribute('src', dataUrl);
          img.removeAttribute('crossorigin');
        });
      };

      for (const block of blocks) {
        const canvas = await html2canvas(block, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: 850,          // same knob as before: larger => smaller on-page font
          onclone,
        });

        let w = contentW;
        let h = (canvas.height * w) / canvas.width;

        // If a single question is taller than a full page, scale it down to fit
        // one page rather than splitting it.
        if (h > pageH - margin * 2) {
          h = pageH - margin * 2;
          w = (canvas.width * h) / canvas.height;
        }

        // New page if this block won't fit in the remaining space
        if (y + h > pageH - margin && y > margin) {
          pdf.addPage();
          y = margin;
        }

        const x = margin + (contentW - w) / 2;  // center if it was scaled down
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', x, y, w, h);
        y += h + gap;
      }

      pdf.save(`${this.studentName}_${this.courseTitle}.pdf`);
    } catch (e) {
      this._toastr.error('A PDF generálása nem sikerült. Kérjük, próbálja újra.');
    } finally {
      this.isPdfLoading = false;
      this._toastr.clear(infoToast.toastId);
    }
  }

  private async buildImageDataUrlMap(root: HTMLElement): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || map.has(src)) return;
      try {
        const res = await fetch(src, { mode: 'cors' });
        const blob = await res.blob();
        const dataUrl: string = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        map.set(src, dataUrl);
      } catch {
        // image stays out if it can't be fetched; PDF still generates
      }
    }));
    return map;
  }
}
